import React, { useEffect, useState, useRef } from 'react';
import { KTSVG, SimpleAlert } from '../../../../../_mexablock/helpers';
import { Modal } from 'react-bootstrap-v5';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { createAsignacionSimple } from '../../../../service/inventario/asignacion';
import {
  createDevolucion,
  downloadDevolucionReportPDF,
  iccidValidation,
} from '../../../../service/inventario/devolucion';
import { useSelectZonasActivas, useSelectRazon } from '../hooks/DevolucionHook';
import Select from 'react-select';

type Props = {
  show: boolean;
  handleClose: (updateGrid: boolean) => void;
};

type SelectOptions = {
  value: number;
  label: string;
  user?: string;
};

const ZonaSchema = Yup.object().shape({
  description: Yup.string().max(200, 'Máximo 200 caracteres.'),
  zone: Yup.number()
    .required('Este campo es requerido')
    .min(1, 'Seleccione una zona'),
  razon: Yup.number()
    .required('Este campo es requerido')
    .min(1, 'Seleccione una razón'),
});

const CreateModalManual: React.FC<Props> = ({ show, handleClose }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(false);
  const [selectedZona, setSelectedZona] = useState<SelectOptions | null>(null);
  const [selectedRazon, setSelectedRazon] = useState<SelectOptions | null>(
    null
  );
  const [scannedItems, setScannedItems] = useState<any[]>([]);
  const [isAsc, setIsAsc] = useState(true);
  const [zoneId, setzoneId] = useState<number>(0);
  const { data: zonas } = useSelectZonasActivas(show);
  const { data: razon } = useSelectRazon(show);

  const formZona = useFormik({
    initialValues: {
      zone: 0,
      razon: 0,
      description: '',
      stock: [] as string[],
    },
    validationSchema: ZonaSchema,
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      if (scannedItems.length === 0) {
        SimpleAlert(
          'Algunos datos no han sido agregados. Por favor, asegúrese de que toda la información solicitada haya sido capturada.',
          4000,
          'error'
        );
        return;
      }

      setLoading(true);
      values.stock = scannedItems.map((item) => item.iccid);

      try {
        const res = await createDevolucion(values);
        SimpleAlert(res.data.message, 3000, 'success');
        resetFormState();
        downloadDevolucionReportPDF(res.data?.doc?.data?.id);
        handleClose(true);
      } catch (err: any) {
        const { message, details } = err.response.data;
        SimpleAlert(details?.[0]?.message || message, 3000, 'error');
      } finally {
        setLoading(false);
        setSubmitting(false);
      }
    },
  });

  const handleScan = async (code: string) => {
    if (!code) return;
    if (scannedItems.length >= 100) {
      SimpleAlert(
        'Límite alcanzado: no se pueden registrar más de 100 unidades.',
        4000,
        'warning'
      );
      return;
    }

    let cleanedCode = code.trim();
    if (cleanedCode.endsWith('f') || cleanedCode.endsWith('F')) {
      cleanedCode = cleanedCode.slice(0, -1);
    }

    if (!/^\d{19}$/.test(cleanedCode)) {
      SimpleAlert(
        'Código no reconocido. Asegúrese de ingresarlo correctamente e inténtelo de nuevo.',
        4000,
        'error'
      );
      return;
    }

    const alreadyAdded = scannedItems.some((item) => {
      const existingCode = String(item.iccid).trim().replace(/f$/i, '');
      return existingCode === cleanedCode;
    });

    if (alreadyAdded) {
      SimpleAlert(
        'Este código ya ha sido agregado a la lista. Por favor, intente con un nuevo código.',
        2000,
        'error'
      );
      return;
    }
    if (zoneId === 0) {
      SimpleAlert(
        'Selecciona una zona antes de escanear un código.',
        2000,
        'error'
      );
      return;
    }
    setLoading(true);
    try {
      const response = await iccidValidation(cleanedCode, zoneId);

      if (response) {
        const newItem = {
          iccid: cleanedCode,
          numero: response.msisdn || '',
          count: scannedItems.length + 1,
          ubicacion: response.location || 'Sin ubicación',
        };

        setScannedItems((prev) => [...prev, newItem]);
        SimpleAlert('Código agregado correctamente', 1500, 'success');
      } else {
        SimpleAlert(response.message, 3000, 'error');
      }
    } catch (error: any) {
      console.error('Error al validar ICCID:', error);
      const apiMessage =
        error?.response?.data?.message ||
        error?.message ||
        'Se ha producido un error durante la operación. Inténtelo más tarde.';

      SimpleAlert(apiMessage, 3000, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleZonas = (selectedOption: any) => {
    const id = selectedOption.value;
    const user = selectedOption.user;
    setSelectedZona(selectedOption);
    formZona.setFieldValue('zone', id);
    setScannedItems([]);
    setzoneId(id);
    formZona.setFieldValue('responsability_user', user);
  };

  const handleRazon = (selectedOption: any) => {
    const id = selectedOption.value;
    setSelectedRazon(selectedOption);
    formZona.setFieldValue('razon', id);
  };

  const resetFormState = () => {
    formZona.resetForm();
    setSelectedZona(null);
    setSelectedRazon(null);
    setScannedItems([]);
    setzoneId(0);
  };

  const toggleSort = () => {
    if (scannedItems.length === 0) {
      SimpleAlert('No hay datos para ordenar', 2000, 'warning');
      return;
    }

    const total = scannedItems.length;
    const sorted = [...scannedItems].reverse();

    setScannedItems(sorted);
    setIsAsc(!isAsc);
  };

  return (
    <Modal
      id="kt_modal_create"
      tabIndex={-1}
      aria-hidden="true"
      dialogClassName="modal-dialog modal-dialog-centered modal-xl"
      show={show}
      onHide={() => {
        resetFormState();
        handleClose(false);
      }}
    >
      <div className="modal-content">
        <div className="modal-header pb-0 border-0 justify-content-end">
          <div
            className="btn btn-sm btn-icon btn-active-color-primary"
            onClick={() => {
              resetFormState();
              handleClose(false);
            }}
          >
            <KTSVG
              className="svg-icon-2"
              path="/media/icons/duotune/arrows/arr061.svg"
            />
          </div>
        </div>

        <div className="modal-body pt-0">
          <div className="text-center mb-13">
            <h1 className="mb-3">Devolución manual</h1>
            <div className="text-muted fw-semibold fs-5">
              Define los detalles y escanea los códigos de barra para devolver
              unidades de forma rápida y precisa.
            </div>
          </div>

          <form
            className="mx-auto w-100 mw-800px pt-10 pb-10"
            autoComplete="off"
            onSubmit={formZona.handleSubmit}
          >
            {/* ----------------- Detalles ---------------- */}
            <div className="d-flex flex-stack mb-3">
              <h3 className="text-gray-500">Detalles de devolución</h3>
            </div>
            <div className="separator separator-dashed mb-5" />

            <div className="row g-9 mb-9">
              <div className="col-md-4">
                <label className="required fw-bold fs-6 mb-2">
                  Devuelto de
                </label>
                <Select
                  options={zonas || []}
                  placeholder="Seleccione"
                  onChange={handleZonas}
                  value={selectedZona ?? null}
                />
                {formZona.touched.zone && formZona.errors.zone && (
                  <div className="text-danger">{formZona.errors.zone}</div>
                )}
              </div>

              <div className="col-md-4">
                <label className="required fw-bold fs-6 mb-2">Razón</label>
                <Select
                  options={razon || []}
                  placeholder="Seleccione"
                  onChange={handleRazon}
                  value={selectedRazon ?? null}
                />
                {formZona.touched.razon && formZona.errors.razon && (
                  <div className="text-danger">{formZona.errors.razon}</div>
                )}
              </div>

              <div className="col-md-4">
                <label className="fw-bold fs-6 mb-2">Responsable de zona</label>
                <input
                  {...formZona.getFieldProps('responsability_user')}
                  className="form-control mb-3 mb-lg-0"
                  type="text"
                  disabled
                />
              </div>

              <div className="col-md-12">
                <label className="fw-bold fs-6 mb-2">Descripción</label>
                <textarea
                  rows={3}
                  maxLength={200}
                  style={{ resize: 'none' }}
                  placeholder="Ej. Falta de unidades"
                  {...formZona.getFieldProps('description')}
                  className="form-control mb-3 mb-lg-0"
                />
              </div>
            </div>

            {/* ----------------- Escáner ---------------- */}
            <div className="mb-5">
              <div className="d-flex flex-stack">
                <h3 className="text-gray-500">Escanear ICCID</h3>
              </div>
              <div className="separator separator-dashed mb-5" />
              <input
                ref={inputRef}
                type="text"
                className="form-control mb-4"
                maxLength={19}
                minLength={19}
                placeholder="Escanee o escriba el código ICCID y presione Enter"
                disabled={loading}
                autoFocus
                inputMode="numeric"
                pattern="[0-9]*"
                onKeyDown={async (e) => {
                  if (
                    !/[0-9]/.test(e.key) && // no es número
                    e.key !== 'Backspace' &&
                    e.key !== 'Enter' &&
                    e.key !== 'Tab' &&
                    e.key !== 'ArrowLeft' &&
                    e.key !== 'ArrowRight'
                  ) {
                    e.preventDefault();
                    return;
                  }

                  if (e.key === 'Enter') {
                    e.preventDefault();
                    const value = e.currentTarget.value.trim();
                    e.currentTarget.value = '';
                    await handleScan(value);

                    setTimeout(() => {
                      inputRef.current?.focus();
                    }, 50);
                  }
                }}
              />

              <div className="d-flex justify-content-between align-items-center mb-4">
                <span className="fw-bold fs-6">
                  Total escaneados: {scannedItems.length}
                </span>
                <button
                  type="button"
                  className="btn btn-icon btn-primary"
                  onClick={toggleSort}
                >
                  <KTSVG
                    path="/media/icons/duotune/arrows/arr093.svg"
                    svgClassName="w-15px h-15px me-1"
                  />
                </button>
              </div>

              {/* Tabla */}
              <div
                className="table-responsive"
                style={{ maxHeight: '400px', overflowY: 'auto' }}
              >
                <table className="table table-row-dashed align-middle gs-0 gy-4">
                  <thead className="bg-light text-muted sticky-top">
                    <tr className="fw-bold">
                      <th className="text-center" style={{ width: '60px' }}>
                        #
                      </th>
                      <th className="text-start">ICCID</th>
                      <th className="text-start">Número</th>
                      <th className="text-start">Ubicación</th>
                      <th
                        className="text-center"
                        style={{ width: '100px' }}
                      ></th>
                    </tr>
                  </thead>

                  <tbody>
                    {scannedItems.length > 0 ? (
                      scannedItems.map((item, index) => (
                        <tr key={index}>
                          <td className="text-center">
                            <span className="fw-bold text-gray-800">
                              {item.count}
                            </span>
                          </td>

                          <td>
                            <span className="fw-semibold text-gray-900">
                              {item.iccid}
                            </span>
                          </td>

                          <td>
                            <span className="fw-semibold text-gray-700">
                              {item.numero || '-'}
                            </span>
                          </td>
                          <td>
                            <span className="fw-semibold text-gray-900">
                              {item.ubicacion}
                            </span>
                          </td>
                          <td className="text-center">
                            <button
                              type="button"
                              className="btn btn-icon btn-light-danger btn-sm"
                              title="Eliminar"
                              onClick={() => {
                                const updated = scannedItems
                                  .filter((_, i) => i !== index)
                                  .map((item, i) => ({
                                    ...item,
                                    count: i + 1,
                                  }));
                                setScannedItems(updated);
                                SimpleAlert(
                                  'Código eliminado correctamente',
                                  1500,
                                  'info'
                                );
                              }}
                            >
                              <KTSVG
                                path="/media/icons/duotune/general/gen027.svg"
                                className="svg-icon-3"
                              />
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={4} className="text-center py-5 text-muted">
                          No hay códigos escaneados
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Botones */}
            <div className="text-center pt-10">
              <button
                type="reset"
                className="btn btn-light me-3"
                onClick={() => {
                  resetFormState();
                  handleClose(false);
                }}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn btn-primary"
                disabled={loading}
                onClick={async () => {
                  const errors = await formZona.validateForm();
                  const hasErrors = Object.keys(errors).length > 0;

                  if (hasErrors) {
                    SimpleAlert(
                      'Algunos datos no han sido agregados. Por favor, asegúrese de que toda la información solicitada haya sido capturada.',
                      4000,
                      'error'
                    );
                    formZona.setTouched(
                      Object.keys(errors).reduce(
                        (acc, key) => ({ ...acc, [key]: true }),
                        {}
                      ),
                      true
                    );
                    return;
                  }
                  formZona.handleSubmit();
                }}
              >
                {!loading && <span>Confirmar</span>}
                {loading && (
                  <span className="indicator-progress">
                    Espere por favor...
                    <span className="spinner-border spinner-border-sm ms-2"></span>
                  </span>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
};

export { CreateModalManual };
